/**
 * i18n master dictionary — ENGLISH is the source of truth (docs/I18N_PLAN.md).
 * Keys are typed: a screen referencing a missing key is a compile error.
 * hi.ts / mr.ts are Partial — any missing key falls back to this file.
 * Convention: snake_case keys grouped by surface; {var} interpolation.
 */
export const en = {
  // common
  cancel: 'Cancel',
  retry: 'Retry',
  back_to_home: 'Back to home',

  // tab bar
  tab_home: 'Home',
  tab_community: 'Community',
  tab_gate: 'Gate',
  tab_pay: 'Pay',

  // home
  greeting_morning: 'Good morning',
  greeting_afternoon: 'Good afternoon',
  greeting_evening: 'Good evening',
  urgent_notice: 'Urgent notice',
  acknowledge: 'Acknowledge',
  visitor_at_gate: 'Visitor at the gate',
  delivery_waiting: 'A delivery is waiting for approval. Tap to review.',
  quick_actions: 'Quick actions',
  qa_visitor: 'Visitor',
  qa_helpdesk: 'Helpdesk',
  qa_pay: 'Pay',
  qa_book: 'Book',
  qa_preapprove: 'Pre-approve',
  qa_book_amenity: 'Book amenity',
  recent_activity: 'Recent activity',
  nothing_new: 'Nothing new to show yet.',
  couldnt_load_notices: "Couldn't load notices right now.",
  all_services: 'All services',
  grp_gate_visitors: 'Gate & visitors',
  grp_staff_home: 'Staff at home',
  grp_bookings: 'Bookings',
  grp_society: 'Society',
  svc_gate_pass: 'Gate pass',
  svc_parcels: 'Parcels',
  svc_vehicles: 'Vehicles',
  svc_family: 'Family',
  svc_kids: 'Kids',
  svc_move: 'Move in/out',
  svc_my_staff: 'My staff',
  svc_attendance: 'Attendance',
  svc_venues: 'Venues',
  svc_my_bookings: 'My bookings',
  svc_finances: 'Finances',
  svc_documents: 'Documents',
  svc_directory: 'Directory',
  svc_sos: 'SOS',
  push_primer_title: "Know when someone's at your gate",
  push_primer_body: 'Visitor arrivals, urgent notices, guest passes — as they happen.',
  push_primer_cta: 'Turn on alerts',
  push_primer_later: 'Later',

  // visitor approval takeover
  main_gate_now: 'Main gate · now',
  close_wo_deciding: 'Close without deciding',
  for_flat: '{purpose} · for {flat}',
  photo_taken: 'Photo taken at gate',
  logged_by: 'Logged by',
  gate_desk: 'Gate desk',
  arrived_at: 'Arrived at',
  id_checked: 'ID checked',
  auto_deny_in: 'Auto-deny in',
  allow_in: 'Allow in',
  allowed_notified: 'Allowed — guard notified',
  leave_at_gate: 'Leave at gate',
  kept_at_gate: 'Kept at gate',
  deny: 'Deny',
  denied: 'Denied',
  decide_error: 'Could not send your decision. Check your connection and try again.',
  no_one_waiting: 'No one is waiting at the gate',
  no_one_waiting_body: 'When someone arrives for your flat, their approval request will show up here.',
  couldnt_load_visitors: 'Could not load visitors',
  couldnt_load_visitors_body: 'We could not reach the visitor list right now. Try again in a bit.',

  // SOS
  emergency: 'Emergency',
  sos_alert: 'SOS Alert',
  sos_hold_instruction: 'Hold the button below to send an emergency alert to security and committee.',
  hold: 'HOLD',
  call_112: 'Call 112',
  sos_when_send: 'When you send SOS:',
  sos_send_guards: 'All guards get an urgent alert',
  sos_send_committee: 'Committee is notified',
  sos_send_contact: 'Your emergency contact gets a call',
  sos_send_flatinfo: 'Your flat info is shared with first responders',
  quick_dial: 'Quick dial',
  sos_sent: 'SOS sent',
  sos_on_way: 'The security team is on the way.',
  cancel_sos: 'Cancel SOS',
  sos_error: 'Could not send SOS. Please try again or call the gate directly.',
  sos_type_medical: 'Medical',
  sos_type_fire: 'Fire',
  sos_type_security: 'Security',
  sos_type_other: 'Other',

  // login
  login_tagline: 'Every gate in your society, one calm app.',
  sign_in: 'Sign in',
  login_sub: 'Use the email your society has on file.',
  email: 'Email',
  password: 'Password',
  enter_password: 'Enter password',
  show: 'Show',
  hide: 'Hide',
  forgot_password: 'Forgot password?',
  or: 'or',
  new_to_bero: 'New to Bero?',
  create_account: 'Create an account',

  // guard kiosk
  main_gate: 'Main gate',
  on_duty: 'on duty',
  sign_out: 'Sign out',
  stat_inside: 'Inside',
  stat_awaiting: 'Awaiting',
  stat_today: 'Today',
  new_entry: 'New entry',
  kiosk_visitor: 'Visitor',
  kiosk_visitor_sub: 'Walk-in entry',
  kiosk_delivery: 'Delivery',
  kiosk_delivery_sub: 'Courier at gate',
  kiosk_cab: 'Cab / Driver',
  kiosk_cab_sub: 'Pickup or drop',
  gate_desk_hdr: 'Gate desk',
  validate_pass: 'Validate pass',
  validate_pass_sub: 'PIN from guest',
  staff_punch: 'Staff punch',
  staff_punch_sub: 'Gate code in/out',
  todays_log: "Today's log",
  todays_log_sub: 'All entries',
  incident: 'Incident',
  incident_sub: 'Alert the office',
  messages: 'Messages',
  kid_exits: 'Kid exits',
  no_entries_today: 'No entries yet today.',
  mark_entered: 'Mark entered',
  mark_left: 'Mark left',

  // guard entry form
  who_at_gate: 'Who is at the gate?',
  type_visitor: 'Visitor',
  type_delivery: 'Delivery',
  type_cab: 'Cab',
  company: 'Company',
  cab_service: 'Cab service',
  person_name_opt: 'Person name (optional)',
  name_lbl: 'Name',
  full_name: 'Full name',
  flat_lbl: 'Flat',
  type_or_pick: 'Type or pick below',
  wing_chip: 'Wing {code}',
  phone_opt: 'Phone (optional)',
  vehicle_number: 'Vehicle number',
  visitor_photo: 'Visitor photo',
  take_photo: 'Take photo',
  add_photo: 'Add photo',
  retake_photo: 'Retake photo',
  companions: 'Companions (+N)',
  leave_at_gate_lbl: 'Leave at gate',
  leave_at_gate_sub: 'Log the parcel without asking the resident.',
  ask_resident: 'Ask resident to approve',
  log_parcel: 'Log parcel at gate',
  err_flat_not_found: 'Flat not found — check the flat code.',
  err_fill_name: 'Fill the name (or company) and flat code.',
  err_entry_generic: 'Could not log the entry. Try again.',
  err_camera: 'Could not open the camera.',

  // enable-alerts full-screen permission gate
  alerts_title: 'Turn on notifications',
  alerts_sub: 'Bero cannot do its job without them. This is how the gate reaches you.',
  alerts_r1_title: 'Visitor at your gate',
  alerts_r1_body: 'Approve or deny guests, deliveries and cabs in seconds — they wait at the gate until you reply.',
  alerts_r2_title: 'Parcels & passes',
  alerts_r2_body: 'Know when a parcel reaches the gate desk and when your guest checks in.',
  alerts_r3_title: 'Urgent society notices',
  alerts_r3_body: 'Water cuts, power shutdowns, emergencies — the moment the committee posts them.',
  alerts_r4_title: 'Family SOS',
  alerts_r4_body: 'If someone in your flat presses SOS, you know immediately.',
  alerts_cta: 'Turn on notifications',
  alerts_cta_settings: 'Open Settings to allow',
  alerts_settings_hint: 'Notifications are currently off for Bero in your phone settings.',
  alerts_skip: 'Not now',

  // shared across guard screens
  allow_entry: 'Allow entry',
  back_to_gate: 'Back to gate',
  filter_all: 'All',
  filter_approved: 'Approved',
  filter_rejected: 'Rejected',
  filter_awaiting: 'Awaiting',

  // guard — validate pass
  validate_scan_qr: 'Scan QR code',
  validate_aim_qr: 'Point at the pass QR',
  validate_cam_off: 'Camera permission is off — enable it in Settings, or use the PIN below.',
  validate_pin_label_or: "Or enter the guest's 6-digit PIN",
  validate_pin_label: "Guest's 6-digit PIN",
  validate_pin_help: 'On their pass, under the QR.',
  validate_check: 'Check pass',
  validate_not_found: 'No pass with that code.',
  validate_check_error: 'Could not check the code. Try again.',
  validate_entered_logged: 'Entered — logged',
  validate_daily_window: 'Daily window: {window}',

  // guard — waiting for resident
  waiting_title: 'Waiting for resident',
  waiting_buzzing: "Resident's phone is buzzing…",
  waiting_sent_ago: 'Sent {n}s ago',
  waiting_approved: 'Approved by resident',
  waiting_rejected: 'Rejected — do not allow entry',
  waiting_left_at_gate: 'Left at the gate — resident chose no entry.',

  // guard — gate log
  log_title: 'Gate log',
  log_empty_title: 'Nothing here',
  log_empty_body: 'Entries matching this filter will show up here.',
  log_report_link: 'Report a mistake',
  log_report_hdr: 'Report a mistaken entry',
  log_report_explain: 'The entry stays in the log — it gets flagged and the society office is notified. Entries are never edited or deleted.',
  log_report_field: 'What went wrong?',
  log_report_placeholder: 'e.g. wrong flat keyed in — visitor was for B-wing',
  log_report_need_reason: 'Say what went wrong first.',
  log_reported_ok: 'Reported — the office has been informed.',
  log_report_error: 'Could not report this entry.',
  log_reporting: 'Reporting…',
  log_report_submit: 'Report entry',

  // guard — messages from flats
  msgs_title: 'Messages from flats',
  msgs_empty_title: 'No messages',
  msgs_empty_body: 'Notes residents send to the gate will show up here.',

  // guard — kid exits
  kids_title: 'Kid exits',
  kids_intro: 'Parents allowed these kids out. Confirm when the child leaves — the flat gets notified.',
  kids_empty_title: 'No active permissions',
  kids_empty_body: 'One-time kid exit permissions from parents show up here.',
  kids_until: 'until {time}',
  kids_confirming: 'Confirming…',
  kids_confirm: 'Confirm exit',

  // guard — staff punch
  punch_code_label: 'Staff gate code',
  punch_code_help: 'Their personal 4-digit code.',
  punch_action: 'Punch',
  punch_not_found: 'No staff member with that code.',
  punch_error: 'Could not punch. Try again.',
  punch_checked_in: 'CHECKED IN',
  punch_checked_out: 'CHECKED OUT',

  // guard — incidents
  incidents_title: 'Incidents',
  incidents_log: 'Log an incident',
  incidents_sheet_sub: 'Admins and the committee are notified the moment you log it.',
  incidents_field: 'What happened?',
  incidents_placeholder: 'e.g. unknown two-wheeler parked at gate 2 for over an hour',
  incidents_need_note: 'Describe the incident first.',
  incidents_logged_ok: 'Logged — admins and the committee have been alerted.',
  incidents_log_error: 'Could not log the incident.',
  incidents_resolved_ok: 'Marked resolved.',
  incidents_resolve_error: 'Could not resolve.',
  incidents_empty_title: 'No incidents',
  incidents_empty_body: 'Anything unusual at the gate — log it here and the office hears about it instantly.',
  incidents_logged_by: 'Logged by {name}',
  incidents_mark_resolved: 'Mark resolved',
  incidents_logging: 'Logging…',
  incidents_log_submit: 'Log incident',
  inc_kind_security: 'Security',
  inc_kind_fire: 'Fire',
  inc_kind_medical: 'Medical',
  inc_kind_maintenance: 'Maintenance',
  inc_kind_other: 'Other',

  // common (added tranche 3)
  close: 'Close',

  // parcels
  parcels_title: 'Parcels at gate',
  parcels_pin_title: 'Your delivery PIN',
  parcels_pin_sub: 'Delivery agents quote this at the gate to leave parcels without a call.',
  parcels_pick_error: 'Could not mark the parcel as picked up. Try again.',
  parcels_empty_title: 'No parcels at the gate',
  parcels_empty_body: 'When a courier leaves a package with the guard, it shows up here.',
  parcels_empty_err_title: 'Parcels unavailable',
  parcels_empty_err_body: 'We could not reach the gate desk right now.',
  parcels_at_desk: 'At the guard desk',
  parcels_collected: 'Collected',
  parcels_mark_picked: 'Mark picked',

  // gate pass — list + single pass UI (the TICKET FACE stays English: it's a printed artifact)
  gp_list_title: 'Gate passes',
  gp_new: '+ New',
  gp_empty_title: 'No gate passes yet',
  gp_empty_body: 'Pre-approve a visitor to create a shareable pass with a QR and PIN.',
  gp_empty_err_title: 'Couldn’t load passes',
  gp_empty_err_body: 'Please try again in a moment.',
  gp_preapprove_cta: 'Pre-approve a visitor',
  gp_title: 'Gate pass',
  gp_share: 'Share',
  gp_share_to_guest: 'Share to guest',
  gp_copied: 'Copied',
  gp_copy_code: 'Copy code',
  gp_copy_error: 'Could not copy the code. Try selecting it manually.',
  gp_share_dialog: 'Gate pass for {name}',
  gp_info_note: 'Guards verify this automatically. No app needed for your guest.',
  gp_cancel_pass: 'Cancel pass',
  gp_revoke_error: 'Could not cancel this pass right now. Try again in a bit.',
  gp_cancelled_title: 'Pass cancelled',
  gp_cancelled_body: 'The pass for {name} has been cancelled and can no longer be used at the gate.',
  gp_preapprove_another: 'Pre-approve another visitor',
  gp_revoke_hdr: 'Cancel this pass?',
  gp_revoke_body: '{name} will no longer be able to use this code or QR at the gate. This can’t be undone.',
  gp_keep_pass: 'Keep pass',
  gp_cancelling: 'Cancelling…',

  // pre-approve visitor (big form)
  pa_title: 'Pre-approve visitor',
  pa_purpose: 'Purpose',
  pa_purpose_guest: 'Guest',
  pa_purpose_delivery: 'Delivery',
  pa_purpose_cab: 'Cab',
  pa_purpose_help: 'Help',
  pa_how_often: 'How often',
  pa_freq_once: 'Once',
  pa_freq_frequent: 'Frequent',
  pa_frequent_note: 'One pass, multiple entries — for the office cab, regular deliveries or long-stay guests.',
  pa_delivery_company: 'Delivery company',
  pa_type_of_help: 'Type of help',
  pa_help_maid: 'Maid',
  pa_help_cook: 'Cook',
  pa_help_driver: 'Driver',
  pa_help_electrician: 'Electrician',
  pa_help_plumber: 'Plumber',
  pa_help_carpenter: 'Carpenter',
  pa_help_other: 'Other',
  pa_help_other_ph: 'e.g. AC technician',
  pa_name_delivery: 'Delivery partner name (optional)',
  pa_name_helper: "Helper's name",
  pa_name_visitor: 'Visitor name',
  pa_name_ph_delivery: 'If you know who is coming',
  pa_name_ph_helper: 'e.g. Ramesh Yadav',
  pa_name_ph_visitor: 'e.g. Sanjay Sharma',
  pa_pick_contacts: 'Pick from contacts →',
  pa_phone: 'Phone number',
  pa_phone_help: 'They’ll get the entry code on this number.',
  pa_guest_n_name: 'Guest {n} name',
  pa_remove_guest: 'Remove guest',
  pa_add_guest: '+ Add another guest — each gets their own pass',
  pa_multi_title: 'Multiple entries',
  pa_multi_sub: 'Guest can come and go during the window with one pass.',
  pa_leave_sub: 'If you don’t answer, the guard keeps the parcel at the gate.',
  pa_quiet_title: 'Quiet arrival',
  pa_quiet_sub: 'Surprise visit — don’t notify the flat when they check in at the gate.',
  pa_expected_on: 'Expected on',
  pa_when_today: 'Today',
  pa_when_tomorrow: 'Tomorrow',
  pa_when_pickdate: 'Pick a date',
  pa_date: 'Date',
  pa_for_next: 'For the next',
  pa_n_days: '{n} days',
  pa_arrives_around: 'Arrives around',
  pa_starts_at: 'Starts at',
  pa_daily_window: 'Daily window',
  pa_valid_for_next: 'Valid for next',
  pa_24_hours: '24 hours',
  pa_one_hour: '1 hour',
  pa_n_hours: '{n} hours',
  pa_daily_note: 'They can enter each day from the arrival time for this many hours.',
  pa_send_code_title: 'Send approval code',
  pa_send_code_sub: 'Visitor gets a 6-digit code via SMS to show at the gate.',
  pa_notes: 'Notes (optional)',
  pa_notes_ph: 'Anything the guard should know',
  pa_submit: 'Pre-approve visitor',
  pa_submit_named: 'Pre-approve {name}',
  pa_err_time: 'Enter a valid start time (e.g. 6:00 PM) before pre-approving.',
  pa_err_generic: 'Could not create the pass. Check your connection and retry.',
  pa_err_unavailable: 'Pre-approvals are not available yet. Please try again soon.',
  pa_err_details: 'Couldn’t create the pass. Check the details and try again.',
  pa_err_conflict: 'That pass could not be created right now. Refresh and try again.',

  // profile tab
  profile_title: 'Profile',
  profile_edit: 'Edit profile',
  profile_verified: 'Verified resident',
  role_owner: 'Owner',
  role_tenant: 'Tenant',
  role_family: 'Family member',
  role_committee_admin: 'Committee admin',
  role_master_admin: 'Master admin',
  sec_account: 'Account',
  sec_notifications: 'Notifications',
  sec_community_safety: 'Community & safety',
  sec_preferences: 'Preferences',
  sec_support: 'Support',
  sec_management: 'Management',
  row_personal_info: 'Personal info',
  row_my_flat: 'My flat',
  row_family: 'Family',
  row_vehicles: 'Vehicles',
  row_notif_inbox: 'Notifications inbox',
  row_residents: 'Residents',
  row_visitor_history: 'Visitor history',
  row_emergency_sos: 'Emergency SOS',
  row_appearance: 'Appearance',
  appearance_dark: 'Dark',
  appearance_light: 'Light',
  row_privacy: 'Privacy',
  row_help: 'Help & FAQ',
  row_contact: 'Contact support',
  row_terms: 'Terms & Privacy',
  row_about: 'About',
  row_society_admin: 'Society admin',
  row_committee_desk: 'Committee desk',
  row_pending_actions: 'Pending actions',
  row_shared_access: 'Shared access',
  logout: 'Logout',

  // language picker
  lang_title: 'Language',
  lang_error: 'Could not change the language. Try again.',
  lang_soon: 'Coming soon',
  lang_footer: 'More languages are coming soon.',

  // notification settings
  np_title: 'Notification settings',
  np_empty_title: 'Settings unavailable',
  np_empty_body: 'We could not load your notification settings right now. Try again in a bit.',
  np_always_on: 'Security and SOS alerts are always on and can’t be muted.',
  np_update_error: 'Could not update the setting. Try again.',
  np_categories: 'Categories',
  np_push: 'Push',
  np_email: 'Email',
  np_push_a11y: '{cat} push notifications',
  np_email_a11y: '{cat} email notifications',
  np_cat_notices: 'Notices',
  np_cat_visitors: 'Visitors & security',
  np_cat_payments: 'Payments',
  np_cat_events: 'Events',
  np_cat_polls: 'Polls',
  np_cat_community: 'Community',
  np_gate_tone: 'Gate tone',
  np_gate_tone_sub: 'The sound that plays when a visitor is waiting for your approval.',
  np_tone_dingdong: 'Ding-dong',
  np_tone_dingdong_sub: 'Classic doorbell — the default',
  np_tone_chime: 'Chime',
  np_tone_chime_sub: 'Bright three-note bell',
  np_tone_marimba: 'Marimba',
  np_tone_marimba_sub: 'Quick wooden run — the softest',
  np_tone_ring: 'Ring',
  np_tone_ring_sub: 'Telephone bell — the loudest',
  np_tone_a11y: '{name} gate tone',
  np_play: 'Play',
  np_play_a11y: 'Play {name}',

  // common (added tranche 5)
  search: 'Search',

  // community tab
  ct_tab_notices: 'Notices',
  ct_tab_discussions: 'Discussions',
  ct_tab_polls: 'Polls',
  ct_tab_events: 'Events',
  ct_tab_issues: 'Issues',
  ml_vendors: 'Vendors',
  ml_classifieds: 'Classifieds',
  ml_lost_found: 'Lost & Found',
  ml_celebrations: 'Celebrations',
  ct_check_connection: 'Check your connection and try again.',
  notice_kind_urgent: 'Urgent',
  notice_kind_event: 'Event',
  notice_kind_committee: 'Committee',
  notice_kind_general: 'General',
  ct_notices_err_title: 'Couldn’t load notices',
  ct_notices_empty_title: 'No notices yet',
  ct_notices_empty_body: 'Notices from your committee will show up here.',
  ct_pinned: 'Pinned',
  ct_all_notices: 'All notices',
  ct_disc_err_title: 'Couldn’t load discussions',
  ct_disc_empty_title: 'No discussions yet',
  ct_disc_empty_body: 'Start one from your community to see it here.',
  ct_reply: '1 reply',
  ct_replies: '{n} replies',
  ct_views: '{n} views',
  ct_nothing_here: 'Nothing here',
  ct_disc_none_body: 'No discussions with this tag yet.',
  ct_polls_err_title: 'Couldn’t load polls',
  ct_active_polls: 'Active polls',
  ct_see_all: 'See all →',
  ct_polls_empty_title: 'No active polls',
  ct_polls_empty_body: 'When the committee starts a poll, it will show up here.',
  ct_anonymous: 'Anonymous',
  ct_voted: '{n} of {total} voted',
  poll_all_wings: 'All wings',
  poll_wing: 'Wing {code}',
  poll_closed: 'Closed',
  poll_open: 'Open',
  poll_closes_today: 'Closes today',
  poll_closes_tomorrow: 'Closes tomorrow',
  poll_closes_in: 'Closes in {n} days',
  ct_events_err_title: 'Couldn’t load events',
  ct_events_empty_title: 'No events yet',
  ct_events_empty_body: 'Upcoming society events will show up here.',
  ct_upcoming: 'Upcoming',
  ct_no_upcoming: 'No upcoming events right now.',
  ct_past: 'Past',
  ct_issues_err_title: 'Couldn’t load issues',
  ct_community_issues: 'Community issues',
  ct_issues_empty_title: 'No community issues',
  ct_issues_empty_body: 'Issues raised for the whole community will show up here.',
  ct_comments: '{n} comments',
  ct_select_subtab: 'Select a sub-tab above — its content opens in the panel on the right.',
  ct_select_notice_title: 'Select a notice',
  ct_select_notice_body: 'Pick a notice from the list to read it here.',
  ct_acknowledged: 'Acknowledged',
  ct_ack_count: '{n} acknowledged',

  // pay tab
  pay_title: 'Payments',
  pay_how_to_pay: 'How to pay',
  pay_bank: 'Bank',
  pay_account: 'Account',
  pay_how_to_pay_note: 'Pay from your bank or UPI app with your flat number in the note. The society treasurer records it and your bill turns PAID here.',
  pay_record_hdr: 'Record payments · treasurer',
  pay_no_unpaid: 'No unpaid bills — everything is recorded.',
  pay_the_flat: 'the flat',
  pay_flat: 'Flat',
  pay_recorded_ok: 'Recorded {amount} from {flat} — bill marked paid.',
  pay_record_error: 'Could not record the payment.',
  pay_due: 'Due {date}',
  pay_includes_late: 'Includes {amount} late fee',
  pay_received: 'Received',
  pay_err_title: 'Payments unavailable',
  pay_err_body: 'We could not load your bill right now. Check your connection and try again.',
  pay_no_dues_title: 'No dues right now',
  pay_no_dues_body: 'There is no bill for the current period. Check back next month, or view your bill history below.',
  pay_fully_paid: '{month} is fully paid',
  pay_recorded_on: 'Recorded {date}',
  pay_no_outstanding: 'No dues outstanding',
  pay_view_breakdown: 'View breakdown',
  pay_past_bills: 'Past bills',
  pay_recent_bills: 'Recent bills',
  pay_view_all: 'View all →',
  pay_no_history: 'No payment history yet.',
  pay_breakdown: '{month} breakdown',
  pay_subtotal: 'Subtotal',
  pay_late_fee: 'Late fee',
  pay_total_due: 'Total due',
  pay_record_title: 'Record {amount} from {flat}',
  pay_record_sub: '{month} · receipts are recorded by the treasurer (bye-law 113) and land on the society’s audit trail.',
  pay_received_via: 'Received via',
  pay_method_cash: 'Cash',
  pay_method_cheque: 'Cheque',
  pay_method_card: 'Card',
  pay_reference: 'Reference',
  pay_reference_help: 'UTR / cheque number — optional but good bookkeeping',
  pay_recording: 'Recording…',
  pay_record_received: 'Record as received',
  pay_due_by: 'Due by {date}',
  pay_invoice_label: 'Invoice',
  pay_overdue_since: 'Overdue since {date}',
  pay_plus_late: '+ late fee',
  pay_col_invoice: 'Invoice',
  pay_col_period: 'Period',
  pay_col_amount: 'Amount',
  pay_col_status: 'Status',

  // bills & receipts history
  bh_title: 'Bills & receipts',
  bh_err_title: 'Bills unavailable',
  bh_err_body: 'We could not load your bill history right now. Try again in a bit.',
  bh_empty_title: 'No bills yet',
  bh_empty_body: 'Your monthly maintenance bills will appear here once they’re generated.',
  bh_paid_in: 'Paid in {year}',
  bh_streak: 'On-time streak',
  bh_months: '{n} mo',
  bh_print_notice: 'Printing needs the next app update — it ships with the new build.',
  bh_col_receipt: 'Receipt',
  bh_receipt: 'receipt',
  bh_invoice: 'invoice',
  bh_download_a11y: 'Download {month} {doc}',
  bh_paid_on: 'Paid {date}',
  bh_incl_late: 'incl. late fee',

  // helpdesk
  hd_title: 'Helpdesk',
  hd_open: 'Open ({n})',
  hd_resolved: 'Resolved ({n})',
  hd_err_title: 'Helpdesk unavailable',
  hd_err_body: 'We could not load your tickets right now. Try again in a bit.',
  hd_no_open: 'No open tickets',
  hd_no_open_body: 'All your issues have been resolved. Tap + to raise a new one.',
  hd_no_resolved: 'No resolved tickets',
  hd_no_resolved_body: 'Tickets you close will show up here.',
  hd_how_moves: 'How a ticket moves',
  hd_j_raised: 'Raised',
  hd_j_raised_sub: 'You describe the problem, add photos',
  hd_j_assigned: 'Assigned',
  hd_j_assigned_sub: 'Facility manager picks a vendor',
  hd_j_resolved: 'Resolved + rated',
  hd_j_resolved_sub: 'You confirm and rate the fix',
  hd_new_ticket: 'New ticket',
  hd_updated: 'Updated {time}',
  hd_category: 'Category',
  hd_field_title: 'Title',
  hd_title_help: 'Keep it short — max 100 characters.',
  hd_title_ph: 'e.g. Kitchen sink leaking',
  hd_description: 'Description',
  hd_desc_ph: 'Describe what’s wrong and where.',
  hd_photos: 'Photos',
  hd_location: 'Location',
  hd_loc_personal: 'Personal (my flat)',
  hd_loc_common: 'Common area',
  hd_priority: 'Priority',
  hd_prio_normal: 'Normal',
  hd_prio_urgent: 'Urgent',
  hd_raise_error: 'Could not raise the ticket. Please try again.',
  hd_raise: 'Raise ticket',
  hd_details: 'Ticket details',
  hd_detail_err_title: 'Ticket unavailable',
  hd_detail_err_body: 'This ticket may have been removed, or something went wrong while loading it.',
  hd_raised_by: 'Raised by',
  hd_you: 'You',
  hd_raised_on: 'Raised on',
  hd_assigned_to: 'Assigned to',
  hd_not_assigned: 'Not yet assigned',
  hd_expected_by: 'Expected by',
  hd_tbc: 'To be confirmed',
  hd_timeline: 'Progress timeline',
  hd_status_updated: 'Status updated',
  hd_commented: '{author} commented',
  hd_awaiting: 'Awaiting resolution',
  hd_pending: 'Pending completion',
  hd_comment_error: 'Could not post your comment. Please try again.',
  hd_attach_photo: 'Attach photo',
  hd_add_comment: 'Add a comment…',
  hd_send_comment: 'Send comment',
  hd_cat_plumbing: 'Plumbing',
  hd_cat_electrical: 'Electrical',
  hd_cat_security: 'Security',
  hd_cat_lift: 'Lift',
  hd_cat_pest: 'Pest',
  hd_cat_cleaning: 'Cleaning',
  hd_cat_other: 'Other',

  // amenity booking
  am_title: 'Book amenity',
  am_my_bookings: 'My bookings',
  am_err_title: 'Amenities unavailable',
  am_err_body: 'We could not load amenity slots right now. Try again in a bit.',
  am_empty_title: 'No amenities set up',
  am_empty_body: 'Your society hasn’t configured any bookable amenities yet.',
  am_per_slot: '/slot',
  am_today_date: 'Today · {date}',
  am_open: 'Open',
  am_taken: 'Taken',
  am_rules: 'Rules',
  am_confirm: 'Confirm booking',
  am_booked_by: 'Booked by {name}',
  am_you: 'you',
  am_charges: 'Charges',
  am_free: 'Free',
  am_conflict: 'That slot was just booked by someone else. Pick another.',
  am_book_error: 'Could not book this slot. Please try again.',
  am_slot_booked: 'Slot booked',
  am_view_bookings: 'View my bookings',
  am_done: 'Done',

  // venue booking
  vb_title: 'Book a venue',
  vb_err_title: 'Venues unavailable',
  vb_err_body: 'We could not load bookable venues right now. Try again in a bit.',
  vb_empty_title: 'No venues set up',
  vb_empty_body: 'Your society hasn’t configured any full-day bookable venues yet.',
  vb_booked: 'Venue booked',
  vb_submitted: 'Booking request submitted',
  vb_confirmed: 'Your booking is confirmed.',
  vb_pending: 'Pending admin confirmation. You will see it under My bookings once reviewed.',
  vb_book_another: 'Book another date',
  vb_choose_venue: 'Choose venue',
  vb_up_to: 'Up to {n} days ahead',
  vb_pick_day: 'Pick a full day',
  vb_purpose: 'Purpose',
  vb_purpose_help: 'Tell the committee what the venue is for.',
  vb_purpose_ph: 'e.g. Daughter’s birthday party',
  vb_info: 'One full-day booking per date per venue. Dates already booked are greyed out.',
  vb_conflict: 'That date was just taken for this venue. Pick another.',
  vb_submit_error: 'Could not submit the booking. Please try again.',
  vb_request: 'Request booking',

  // my bookings
  mb_err_title: 'Bookings unavailable',
  mb_err_body: 'We could not load your bookings right now. Try again in a bit.',
  mb_empty_title: 'No bookings yet',
  mb_empty_body: 'Amenity slots and venue requests you book will show up here.',
  mb_venues_for_functions: 'Venues for functions',
  mb_cancel_error: 'Could not cancel this booking. Please try again.',
  mb_up_to_guests: 'Up to {n} guests',
  mb_days_ahead: '{n}d ahead',
  mb_amenity: 'Amenity',
  mb_venue: 'Venue',
  mb_cancel_confirm: 'Cancel this booking?',
  mb_keep: 'Keep it',
  mb_yes_cancel: 'Yes, cancel',
  mb_cancel_booking: 'Cancel booking',

  // residents directory
  res_search_ph: 'Search by name or flat',
  res_wing: '{w} Wing',
  res_err_title: 'Directory unavailable',
  res_err_body: 'We could not load the resident directory right now. Try again in a bit.',
  res_empty_title: 'No residents yet',
  res_empty_body: 'Approved residents of your society will appear here.',
  res_privacy_note: 'Residents choose what to share. Some details may be hidden.',
  res_no_matches: 'No matches',
  res_no_matches_body: 'Try a different name, flat or wing.',
  res_private: 'Private',

  // family & passes
  fp_title: 'Family & passes',
  fp_add: 'Add family member',
  fp_permanent: 'Permanent passes',
  fp_note: 'Family with a permanent pass enter without approval each time.',
  fp_err_title: 'Family & passes unavailable',
  fp_err_body: 'We could not load your family list right now. Try again in a bit.',
  fp_empty_title: 'No family members yet',
  fp_empty_body: 'Add family so they can enter without approval each time.',
  fp_add_error: 'Could not add this member right now. Try again in a bit.',
  fp_schedules: 'Schedules',
  fp_pass_active: 'Pass active',
  fp_pending: 'Pending',
  fp_relation: 'Relation',
  fp_relation_ph: 'Spouse, Parent, Child…',
  fp_is_kid: 'This is a kid (needs recurring schedules)',
  fp_is_kid_short: 'This is a kid',
  fp_add_member: 'Add member',

  // kids permissions
  kp_title: 'Kids permissions',
  kp_add_window: 'Add window',
  kp_allow_now: 'Allow my kid out now',
  kp_allow_now_sub: 'One-time permission — the gate lets them out within the window.',
  kp_child_name: 'Child’s name',
  kp_allow_named: 'Allow {name} out for {h}h',
  kp_allow_out: 'Allow out',
  kp_info: 'Kids are auto-allowed out during these recurring windows. Guards get a heads-up.',
  kp_err_title: 'Kids permissions unavailable',
  kp_err_body: 'We could not load these right now. Try again in a bit.',
  kp_empty_title: 'No recurring windows',
  kp_empty_body: 'Add a recurring window like school pickup or a class to auto-allow it.',
  kp_toggle_error: 'Could not update this window. Try again.',
  kp_allow_error: 'Could not create the exit permission. Try again in a bit.',
  kp_save_error: 'Could not save the changes. Try again in a bit.',
  kp_add_error: 'Could not add this window right now. Try again in a bit.',
  kp_remove_error: 'Could not remove this window. Try again in a bit.',
  kp_child: 'Child',
  kp_edit_window: 'Edit recurring window',
  kp_add_recurring: 'Add recurring window',
  kp_kid_ph: 'e.g. Aanya',
  kp_activity: 'Activity',
  kp_activity_ph: 'e.g. School bus',
  kp_schedule: 'Schedule',
  kp_schedule_ph: 'Mon–Fri · 7:30–8:00 AM',
  kp_escort: 'Escort / with whom',
  kp_escort_ph: 'Bright Future School',
  kp_save: 'Save changes',
  kp_remove: 'Remove this window',

  // move-in / move-out
  mo_title: 'Move-in / out request',
  mo_request_type: 'Request type',
  mo_movein: 'Move-in',
  mo_moveout: 'Move-out',
  mo_preferred_date: 'Preferred date',
  mo_time_slot: 'Time slot',
  mo_vehicle: 'Goods vehicle number',
  mo_helpers: 'Number of helpers',
  mo_notes: 'Notes (optional)',
  mo_notes_ph: 'Lift booking, fragile items, etc.',
  mo_deposit: 'Refundable deposit',
  mo_applies: 'Applies',
  mo_lift: 'Service lift booking',
  mo_included: 'Included',
  mo_approval: 'Committee approval',
  mo_required: 'Required',
  mo_submitted: '{type} request submitted for admin approval.',
  mo_err_details: 'Couldn’t submit the request. Check the date and time slot and try again.',
  mo_err_generic: 'Could not submit the request right now. Try again in a bit.',
  mo_submit: 'Submit request',
  mo_your_requests: 'Your requests',
  mo_no_requests: 'No move requests yet.',
  mo_request_title: '{type} request',
  mo_date: 'Date',
  mo_goods_vehicle: 'Goods vehicle',
  mo_helpers_label: 'Helpers',
  mo_submitted_label: 'Submitted',
  mo_waiting: 'Waiting for committee approval. You can cancel until it is approved.',
  mo_cancel: 'Cancel this request',
  mo_cancel_error: 'Could not cancel this request. Try again in a bit.',

  // attendance
  at_title: 'Attendance',
  at_prev_month: 'Previous month',
  at_next_month: 'Next month',
  at_err_title: 'Attendance unavailable',
  at_err_body: 'We could not load the attendance log right now.',
  at_empty_title: 'No entries this month',
  at_empty_body: 'No in/out records for {month} yet.',
  at_no_records: 'No records for this month.',
  at_absent: 'Absent',

  // vehicles
  veh_title: 'My vehicles',
  veh_add: 'Add vehicle',
  veh_car: 'Car',
  veh_two_wheeler: 'Two-wheeler',
  veh_other: 'Other',
  veh_plate_error: 'Enter the vehicle number plate.',
  veh_dup_error: 'This plate is already registered in your society.',
  veh_add_error: 'Could not add the vehicle. Check your connection and retry.',
  veh_err_title: 'Vehicles unavailable',
  veh_err_body: 'We could not load your vehicles right now. Try again in a bit.',
  veh_empty_title: 'No vehicles yet',
  veh_empty_body: 'Register your car or two-wheeler so the gate recognises it.',
  veh_add_cta: 'Add a vehicle',
  veh_sticker: 'Parking sticker',
  veh_plate: 'Number plate',
  veh_type: 'Type',
  veh_model: 'Model (optional)',
  veh_model_ph: 'e.g. Hyundai Creta · White',
  veh_sticker_field: 'Parking sticker (optional)',
  veh_sticker_ph: 'e.g. A-601-C1',

  // delivery auto-approval PIN
  dp_title: 'Delivery auto-approval',
  dp_err_title: 'Delivery PIN unavailable',
  dp_err_body: 'We could not load your gate PIN right now. Try again in a bit.',
  dp_auto_approve: 'Auto-approve deliveries',
  dp_auto_approve_sub: 'Delivery agents enter your PIN at the gate to be let in without disturbing you.',
  dp_rotates: 'rotates monthly',
  dp_generating: 'Generating…',
  dp_regenerate: 'Regenerate PIN',
  dp_warn: 'Only share with delivery apps you trust. Every use is logged with a photo.',
  dp_regenerated: 'New PIN generated. Old PIN stops working immediately.',
  dp_regen_error: 'Could not regenerate the PIN right now.',

  // message the gate
  mg_title: 'Message the gate',
  mg_info: 'Goes straight to the guard desk. For emergencies use SOS — this is for everyday instructions.',
  mg_placeholder: 'e.g. Furniture tempo coming around 5 PM — please allow it to the service lift.',
  mg_send_error: 'Could not send that. Try again in a bit.',
  mg_send: 'Send to gate',
  mg_sent: 'Sent',
  mg_empty_title: 'Nothing sent yet',
  mg_empty_body: 'Notes you send to the gate will show up here with a seen state.',

  // party invite
  pi_title: 'Party invite',
  pi_intro: 'One link for the whole gathering — every guest taps it and gets their own gate pass.',
  pi_occasion: 'What’s the occasion?',
  pi_occasion_ph: 'e.g. Aarav’s birthday',
  pi_when: 'When',
  pi_open_for: 'Open for',
  pi_guest_limit: 'Guest limit',
  pi_n_guests: '{n} guests',
  pi_create_named: 'Create invite for {title}',
  pi_create: 'Create invite',
  pi_your_invites: 'Your invites',
  pi_empty_title: 'No invites yet',
  pi_empty_body: 'Create one above — perfect for birthdays, poojas and house parties.',
  pi_claimed: '{n} of {total} passes claimed',
  pi_copied: 'Link copied',
  pi_share: 'Share link',
  pi_cancelling: 'Cancelling…',
  pi_time_error: 'Enter a valid start time (e.g. 6:00 PM).',
  pi_created: 'Invite created — share the link with your guests.',
  pi_create_error: 'Could not create the invite right now. Try again in a bit.',
  pi_cancel_error: 'Could not cancel that invite. Try again in a bit.',

  // classifieds
  cl_err_title: 'Couldn’t load classifieds',
  cl_err_body: 'Something went wrong while fetching listings. Check your connection and try again.',
  cl_empty_title: 'No listings yet',
  cl_empty_body: 'Items residents want to sell or give away will show up here.',

  // lost & found
  lf_err_title: 'Couldn’t load Lost & Found',
  lf_err_body: 'Something went wrong while fetching items. Check your connection and try again.',
  lf_empty_title: 'Nothing reported',
  lf_empty_body: 'Lost or found items reported by residents will show up here.',

  // celebrations
  bd_share: 'Share my celebrations',
  bd_share_sub: 'Neighbours can wish you on your special days.',
  bd_err_title: 'Couldn’t load celebrations',
  bd_err_body: 'Something went wrong while fetching celebrations. Check your connection and try again.',
  bd_empty_title: 'No celebrations this week',
  bd_empty_body: 'Birthdays and anniversaries in the society will show up here.',
  bd_this_week: 'This week',
  bd_wished_count: '{n} wished',
  bd_wished: 'Wished',
  bd_wish: 'Wish',

  // personal info
  pinfo_name_error: 'Enter your full name.',
  pinfo_save_error: 'Could not save your profile right now. Try again in a bit.',
  pinfo_change_photo: 'Change photo',
  pinfo_photo_unavailable: 'Photo upload isn’t available yet.',
  pinfo_phone: 'Phone',
  pinfo_email_help: 'Email can’t be changed — it’s your sign-in.',
  pinfo_show_phone: 'Show my phone in the resident directory',
  pinfo_visible: 'Visible',
  pinfo_flat_note: 'Flat and ownership details are managed by your society admin.',

  // change password
  cp_title: 'Change password',
  cp_current: 'Current password',
  cp_new: 'New password',
  cp_confirm: 'Confirm new password',
  cp_update: 'Update password',
  cp_current_error: 'Enter your current password.',
  cp_weak: 'At least 8 characters with a number.',
  cp_mismatch: 'Passwords don’t match.',
  cp_wrong_current: 'Current password is incorrect.',
  cp_generic_error: 'Could not update the password right now.',
  cp_done_title: 'Password updated',
  cp_done_body: 'Use your new password the next time you sign in.',

  // deactivate account
  da_title: 'Deactivate account',
  da_your_flat: 'your flat',
  da_password_error: 'Enter your password to confirm.',
  da_wrong_password: 'Password is incorrect.',
  da_generic_error: 'Could not submit the request right now. Try again in a bit.',
  da_heading: 'Request account deactivation',
  da_body: 'Your society admin will review this request. Once approved, you’ll lose access to notices, payments and gate features for {flat}. Pending dues must be cleared first.',
  da_reason: 'Reason (optional)',
  da_reason_ph: 'Moving out, selling flat, etc.',
  da_confirm_pw: 'Confirm with your password',
  da_request: 'Request deactivation',

  // my flat
  mf_title: 'My flat',
  mf_err_title: 'Flat details unavailable',
  mf_err_body: 'We could not load your flat right now. Try again in a bit.',
  mf_registered: 'Your registered flat',
  mf_ownership: 'Ownership',
  mf_carpet: 'Carpet area',
  mf_parking: 'Parking',
  mf_residents: 'Residents & family',
  mf_you: 'you',

  // notifications inbox
  nb_mark_all: 'Mark all read',
  nb_read_error: 'Could not mark as read. Try again.',
  nb_read_all_error: 'Could not mark all as read. Try again.',
  nb_err_title: 'Notifications unavailable',
  nb_err_body: 'We could not load your notifications right now. Try again in a bit.',
  nb_empty_title: 'All caught up',
  nb_empty_body: 'Approvals, notices and payment updates will land here.',

  // emergency contacts
  em_title: 'Emergency contacts',
  em_all_contacts: 'All contacts',
  em_security: 'Security',
  em_no_guards: 'No guards listed',
  em_no_guards_body: 'Gate staff will show up here once your society adds them.',
  em_guard_note: 'Your gate, one tap away — call the guard on duty directly.',
  em_err_title: 'Contacts unavailable',
  em_err_body: 'We could not load your emergency contacts right now. Try again in a bit.',
  em_empty_title: 'No emergency contacts',
  em_empty_body: 'Contacts added here are called automatically when you trigger SOS.',
  em_sos_note: 'These contacts are called automatically when you trigger SOS.',

  // society directory
  dir_flats: '{n} flats',
  dir_residents: '{n} residents',
  dir_empty_title: 'Nothing here yet',
  dir_err_body: 'We could not load the society directory right now. Try again in a bit.',
  dir_empty_body: 'Society contacts and wing details will appear here.',
  dir_wings: 'Wings',
  dir_contacts: 'Society contacts',
  dir_resident_dir: 'Resident directory',

  // global search
  sr_back: 'Back',
  sr_placeholder: 'Search notices, people, events…',
  sr_idle_title: 'Search your society',
  sr_idle_body: 'Find notices, events, vendors and residents. Type at least two characters.',
  sr_err_title: 'Search unavailable',
  sr_err_body: 'We could not search right now. Try again in a bit.',
  sr_no_matches_body: 'Nothing found for “{q}”. Try a name, flat or notice title.',

  // document vault
  doc_upload: 'Upload',
  doc_too_big: 'That file is over 5 MB — compress it and try again.',
  doc_picker_error: 'Could not open the file picker.',
  doc_title_error: 'Give the document a title.',
  doc_uploaded: '“{name}” is in the vault.',
  doc_upload_error: 'Could not upload.',
  doc_removed: '“{name}” removed — recorded on the audit trail.',
  doc_remove_error: 'Could not remove.',
  doc_err_title: 'Documents unavailable',
  doc_err_body: 'We could not load your documents right now. Try again in a bit.',
  doc_empty_title: 'No documents yet',
  doc_empty_body: 'Receipts, society letters and NOCs shared with you will appear here.',
  doc_search_ph: 'Search documents',
  doc_no_matches_body: 'No documents match “{q}”.',
  doc_remove: 'Remove',
  doc_upload_hdr: 'Upload to the vault',
  doc_upload_sub: 'PDF or image, up to 5 MB. Every member can read the vault; uploads are recorded on the audit trail (bye-law 140(17)).',
  doc_change: 'Change',
  doc_choose_file: 'Choose file',
  doc_title_field: 'Title',
  doc_title_ph: 'e.g. AGM 2025-26 minutes (signed)',
  doc_uploading: 'Uploading…',
  doc_cat_minutes: 'Minutes',
  doc_cat_legal: 'Legal',
  doc_cat_certificate: 'Certificate',
  doc_cat_contract: 'Contract',
  doc_cat_circular: 'Circular',
  doc_cat_finance: 'Finance',
  doc_cat_general: 'General',

  // document viewer
  dv_title: 'Document',
  dv_download: 'Download',
  dv_download_unavailable: 'Downloads aren’t available yet.',
  dv_preview_soon: 'Preview coming soon',
  dv_preview_soon_body: 'In-app previews aren’t ready yet — download from the society office in the meantime.',

  // shared bits
  phatt_photos: 'Photos',
  phatt_camera: 'Camera',
  phatt_choose: 'Choose photo',
  phatt_remove: 'Remove photo',
  phatt_cam_declined: 'Camera permission was declined.',
  phatt_read_error: 'Could not read that photo. Try another one.',
  phatt_picker_error: 'Could not open the photo picker.',
  dc_busy: 'busy',
  up_a11y: 'Upvote',

  // relative time (timeAgo)
  time_now: 'Just now',
  time_m: '{n}m ago',
  time_h: '{n}h ago',
  time_d: '{n}d ago',
  time_today: 'Today',
  time_yesterday: 'Yesterday',

  // gate pass window labels (list row)
  gp_multi_entry: 'Multi entry',
  gp_single_entry: 'Single entry',

  // visitor detail
  vd_title: 'Visit details',
  vd_type_guest: 'Guest',
  vd_type_delivery: 'Delivery',
  vd_type_cab: 'Cab',
  vd_type_staff: 'Staff visit',
  vd_view_photo: 'View photo',
  vd_no_photo: 'No photo yet',
  vd_err_title: 'Visit not found',
  vd_err_body: 'This entry may have been removed, or you may not have access to it.',
  vd_back: 'Back to visitors',
  vd_call: 'Call',
  vd_call_a11y: 'Call {name}',
  vd_expected_from: 'Expected from',
  vd_expected_until: 'Expected until',
  vd_arrived: 'Arrived at gate',
  vd_checked_in: 'Checked in',
  vd_checked_out: 'Checked out',
  vd_no_timings: 'No gate timings recorded for this visit.',
  vd_company: 'Company',
  vd_vehicle: 'Vehicle',
  vd_id_checked: 'ID checked',
  vd_approved_by: 'Approved by',
  vd_no_details: 'No extra details captured at the gate.',
  vd_notes: 'Notes',
  vd_captured: 'Captured by the guard at entry.',

  // community issue categories (API-value enum)
  ic_cleanliness: 'Cleanliness',
  ic_lift: 'Lift',
  ic_noise: 'Noise',
  ic_water: 'Water',
  ic_parking: 'Parking',
  ic_electrical: 'Electrical',
  ic_garbage: 'Garbage',
  ic_plumbing: 'Plumbing',
  ic_housekeeping: 'Housekeeping',
  ic_internet: 'Internet',
  ic_security: 'Security',
  ic_maintenance: 'Maintenance',
  ic_general: 'General',
  ic_other: 'Other',

  // discussion tags (API-value enum)
  dt_general: 'General',
  dt_lost_found: 'Lost & Found',
  dt_recommendations: 'Recommendations',
  dt_help: 'Help',
  dt_events: 'Events',
  dt_announcements: 'Announcements',
  dt_activities: 'Activities',

  // celebration kind (API-value enum)
  ck_birthday: 'Birthday',
  ck_anniversary: 'Anniversary',

  // notification category (API-value enum)
  nc_visitor: 'Visitor',
  nc_security: 'Security',
  nc_notice: 'Notice',
  nc_payment: 'Payment',
  nc_booking: 'Booking',
  nc_event: 'Event',
  nc_poll: 'Poll',
  nc_community: 'Community',
  nc_system: 'System',
  nc_helpdesk: 'Helpdesk',
  nc_maintenance: 'Maintenance',

  // date grid weekday letters (Sun–Sat, single char)
  wd_s0: 'S', wd_m: 'M', wd_t2: 'T', wd_w: 'W', wd_t4: 'T', wd_f: 'F', wd_s6: 'S',

  // outbound share text
  gp_share_text_link: 'Bero gate pass for {name}. Show this at the gate:\n{link}',
  gp_share_text_code: 'Bero gate pass for {name} — code {code}. {valid}.',
  gp_share_title: 'Bero gate pass',
  pi_share_text: 'You’re invited — {title}! Tap for your personal gate pass:\n{link}',

  // Gate tab (hub)
  gt_at_gate_now: 'At the gate now',
  gt_none_waiting: "No one's waiting at the gate",
  gt_none_waiting_sub: "You'll be alerted the moment someone arrives.",
  gt_tap_review: 'tap to review',
  gt_preapprove: 'Pre-approve a visitor',
  gt_gate_security: 'Gate & security',
  gt_svc_gatepass: 'Gate pass',
  gt_svc_gatepass_sub: 'Share a QR / PIN',
  gt_svc_party: 'Party invite',
  gt_svc_party_sub: 'One link, many guests',
  gt_svc_message: 'Message the gate',
  gt_svc_message_sub: 'A note to the guard desk',
  gt_svc_history: 'Visitor history',
  gt_svc_history_sub: 'Who came & went',
  gt_svc_staff: 'Household staff',
  gt_svc_staff_sub: 'Maids, cooks, drivers',
  gt_svc_attendance: 'Attendance',
  gt_svc_attendance_sub: 'Staff in / out',
  gt_svc_parcels: 'Parcels',
  gt_svc_parcels_sub: 'Held at the gate',
  gt_svc_delivery_pin: 'Delivery PIN',
  gt_svc_delivery_pin_sub: 'Auto-approve drops',
  gt_svc_family: 'Family passes',
  gt_svc_family_sub: 'Permanent entry',
  gt_svc_kids: 'Kids',
  gt_svc_kids_sub: 'Recurring permissions',
  gt_svc_move: 'Move in / out',
  gt_svc_move_sub: 'Request a slot',

  // Onboarding — shared steps rail (society / flat / verify / signup)
  onb_step_society: 'Find your society',
  onb_step_society_sub: 'Search by name or city',
  onb_step_society_chosen: 'Society chosen',
  onb_step_flat: 'Choose your flat',
  onb_step_flat_sub: 'Wing and number',
  onb_step_flat_chosen: 'Flat chosen',
  onb_step_verify: 'Verify residency',
  onb_step_verify_sub: 'Role and one document',
  onb_step_account: 'Create your account',
  onb_step_account_sub: 'Name, email and password',

  // Onboarding — shared bits
  onb_back: 'Back',
  onb_done: 'Done',
  onb_choose_society: 'Choose society',
  onb_start_over_title: "Let's start over",
  onb_check_connection: 'Check your connection and try again.',
  onb_your_society: 'Your society',
  onb_sign_out: 'Sign out',
  onb_foot_note: 'Reviewed by your society committee. Bero never shows ads or sells data.',

  // Onboarding — society select
  onb_soc_title: 'Which society do you live in?',
  onb_soc_sub_desktop: "We'll set up your account for that community.",
  onb_soc_sub_mobile: "Step 1 of 3 · We'll set up your account for that community.",
  onb_soc_search_ph: 'Search by society name or city',
  onb_results: 'Results',
  onb_near_you: 'Near you',
  onb_soc_cant_find: "Can't find your society?",
  onb_soc_ask_bring: 'Ask us to bring Bero there',
  onb_soc_not_on_bero: 'NOT ON BERO YET',
  onb_soc_flats: '{count} flats',
  onb_soc_err_title: "Couldn't load societies",
  onb_soc_none_title: 'No societies found',
  onb_soc_none_body: 'Try a different name or check the spelling.',

  // Onboarding — lead capture ("bring Bero to my society")
  onb_lead_done_title: "Got it — we're on it",
  onb_lead_done_body: "We'll reach out about bringing Bero to {society}. No spam — one call, that's it.",
  onb_lead_your_society: 'your society',
  onb_lead_title: 'Bring Bero to your society',
  onb_lead_sub: "Tell us where you live and we'll talk to your committee.",
  onb_lead_society_label: 'Society name',
  onb_lead_society_ph: 'e.g. Palm Meadows CHS',
  onb_lead_city_label: 'City',
  onb_lead_city_ph: 'e.g. Pune',
  onb_lead_name_label: 'Your name (optional)',
  onb_lead_name_ph: 'So we know who to ask for',
  onb_lead_phone_label: 'Phone',
  onb_lead_error: 'Could not send that right now. Try again in a bit.',
  onb_lead_submit: 'Request Bero',

  // Onboarding — flat select
  onb_flat_no_soc_title: 'Choose a society first',
  onb_flat_no_soc_body: 'Go back and pick your society to continue.',
  onb_wing_n: 'Wing {code}',
  onb_flat_title: 'Which flat is yours?',
  onb_flat_claimed_locked: 'Claimed flats are locked',
  onb_flat_sub_mobile: 'Step 2 of 3 · Claimed flats are locked',
  onb_flat_err_title: "Couldn't load flats",
  onb_flat_none_title: 'No flats found',
  onb_flat_none_body: "This wing doesn't have any flats listed yet.",
  onb_flat_continue_with: 'Continue with {code}',
  onb_flat_select_cta: 'Select a flat to continue',

  // Onboarding — verify residency
  onb_role_owner: 'Owner',
  onb_role_owner_sub: 'Flat is registered in my name',
  onb_role_tenant: 'Tenant',
  onb_role_tenant_sub: 'I rent this flat',
  onb_role_family: 'Family member',
  onb_role_family_sub: 'I live with the owner or tenant',
  onb_verify_title: 'Verify you live here',
  onb_verify_reviewed: 'Reviewed by your committee',
  onb_verify_sub_mobile: 'Step 3 of 3 · Reviewed by your committee',
  onb_verify_i_am: 'I am the',
  onb_verify_proof: 'Proof of residence',
  onb_verify_upload_title: 'Upload agreement or electricity bill',
  onb_verify_upload_hint: 'PDF or photo · max 10 MB',
  onb_verify_upload_unavailable: "Document upload isn't available yet.",
  onb_verify_encrypt_note: 'Your documents are encrypted and shared only with your society admin. Never with third parties.',
  onb_verify_start_over_body: 'Pick your society and flat again to continue.',
  onb_verify_submit: 'Submit for approval',

  // Onboarding — create account
  onb_signup_title: 'Create your account',
  onb_signup_sub: "Last step — this is what you'll use to sign in.",
  onb_signup_start_over_body: 'Pick your society again to create your account.',
  onb_signup_flat: 'Flat {code}',
  onb_signup_name_label: 'Full name',
  onb_signup_name_ph: 'e.g. Amit Sharma',
  onb_signup_email_label: 'Email address',
  onb_signup_phone_label: 'Phone number',
  onb_signup_phone_help: "They'll use this to reach you at the gate.",
  onb_signup_password_label: 'Password',
  onb_signup_min8: 'At least 8 characters.',
  onb_signup_err_name: 'Enter your full name.',
  onb_signup_err_email: 'Enter a valid email address.',
  onb_signup_err_phone: 'Enter a valid phone number.',
  onb_signup_err_email_taken: 'This email is already registered.',
  onb_signup_err_society_404: 'This society could not be found. Please start over.',
  onb_signup_err_generic: 'Could not create your account right now. Try again in a bit.',
  onb_signup_submit: 'Create account',

  // Onboarding — pending approval
  onb_pending_title: 'Waiting at the gate',
  onb_pending_body: "Your committee reviews new residents. Most approvals happen within a day. We'll email you the moment you're in.",
  onb_pending_badge: 'Pending',
  onb_pending_submitted: 'submitted {when}',
  onb_pending_notify: 'Notify me when approved',

  // polls (index)
  poll_active: 'Active',
  poll_err_body: 'Something went wrong while fetching polls. Check your connection and try again.',
  poll_empty_closed_title: 'No closed polls',
  poll_empty_closed_body: 'Results will appear here once a poll closes.',
  poll_vote_error: "Couldn't record your vote. Please try again.",
  poll_submit_vote: 'Submit vote',

  // events (index)
  event_err_body: 'Something went wrong while fetching events. Check your connection and try again.',
  event_empty_upcoming_title: 'No upcoming events',
  event_empty_upcoming_body: 'When the committee schedules an event, it will show up here.',
  event_empty_past_title: 'No past events',
  event_empty_past_body: 'Events move here once they have happened.',
  event_create: 'Create event',
  event_going: '{n} going',

  // issues (index)
  issue_tab_personal: 'Personal',
  issue_err_body: 'Something went wrong while fetching issues. Check your connection and try again.',
  issue_empty_personal_title: 'No personal tickets',
  issue_empty_personal_body: 'Issues you report for your flat will show up here.',
  issue_upvote_error: "Couldn't record your upvote. Please try again.",
  issue_report: 'Report issue',
  issue_notify_wing: 'Wing residents notified',
  issue_notify_all: 'All residents notified',
  issue_notify_helpdesk: 'Helpdesk notified',
  issue_notify_wing_x: 'Wing {wing} residents notified',
  ev_cat_festival: 'Festival',
  ev_cat_sports: 'Sports',
  ev_cat_kids: 'Kids',
  ev_cat_cultural: 'Cultural',
  ev_cat_meeting: 'Meeting',
  ev_cat_general: 'General',

  // vendors (index)
  vendor_title: 'Trusted vendors',
  vendor_cat_electrician: 'Electrician',
  vendor_cat_housekeeping: 'Housekeeping',
  vendor_cat_appliance: 'Appliance',
  vendor_cat_gardening: 'Gardening',
  vendor_cat_plumbing: 'Plumbing',
  vendor_cat_carpentry: 'Carpentry',
  vendor_cat_painting: 'Painting',
  vendor_cat_pest: 'Pest Control',
  vendor_err_title: "Couldn't load vendors",
  vendor_err_body: 'Something went wrong while fetching vendors. Check your connection and try again.',
  vendor_empty_title: 'No vendors here',
  vendor_empty_body: 'Vendors endorsed by residents in this category will show up here.',
  vendor_pinned: 'PINNED',
  vendor_endorsement: '{n} resident endorsement',
  vendor_endorsements: '{n} resident endorsements',
  vendor_call: 'Call {name}',

  // visitors (index)
  vis_title: 'Visitors',
  vis_filter: 'Filter',
  vis_err_title: 'Visitor history unavailable',
  vis_err_body: 'We could not reach the gate records right now. Pull back later.',
  vis_empty_title: 'No visitors yet',
  vis_empty_body: 'Approved, rejected and pre-approved visitors will show up here.',
  vis_preapprove: 'Pre-approve a visitor',
  vis_group_earlier: 'Earlier',
  vis_load_earlier: 'Load earlier visitors',

  // poll (detail)
  poll_detail_title: 'Poll',
  poll_unavailable_title: 'Poll unavailable',
  poll_unavailable_body: 'This poll may have been removed, or something went wrong while loading it.',
  poll_posted_by: 'Posted by {name}',
  poll_total_votes: '{n} total votes',
  poll_anon_note: 'responses are anonymous',

  // event (detail)
  event_detail_title: 'Event',
  event_unavailable_title: 'Event unavailable',
  event_unavailable_body: 'This event may have been removed, or something went wrong while loading it.',
  event_going_residents: '{n} residents going',
  event_whos_going: "Who's going",
  event_more: '+{n} more',
  event_rsvp_going: 'Going',
  event_rsvp_maybe: 'Maybe',
  event_rsvp_cant: "Can't go",
  event_rsvp_error: "Couldn't save your RSVP. Please try again.",

  // issue (detail)
  issue_detail_title: 'Issue',
  issue_unavailable_title: 'Issue unavailable',
  issue_comment_error: "Couldn't post your comment. Please try again.",
  issue_status_reported: 'Reported',
  issue_status_progress: 'In progress',
  issue_status_urgent: 'Urgent',
  issue_status_resolved: 'Resolved',

  // notice (detail)
  notice_detail_title: 'Notice',
  notice_unavailable_title: 'Notice unavailable',
  notice_unavailable_body: 'This notice may have been removed, or something went wrong while loading it.',
  notice_read_by_one: 'Read by {n} resident',
  notice_read_by_many: 'Read by {n} residents',
  notice_attachments: 'Attachments',
  notice_open: 'Open',
  notice_ack_error: "Couldn't record that. Please try again.",

  // vendor (detail)
  vendor_detail_title: 'Vendor',
  vendor_unavailable_title: 'Vendor unavailable',
  vendor_unavailable_body: 'This vendor may have been removed, or something went wrong while loading it.',
  vendor_endorsements_count: '{n} endorsements',
  vendor_endorsed: 'Endorsed',
  vendor_endorse: 'Endorse',
  vendor_resident_notes: 'Resident notes',
  vendor_endorse_error: "Couldn't update your endorsement. Please try again.",

  // issues/report (create form)
  ir_title: 'Report issue',
  ir_discard: 'Discard',
  ir_title_ph: 'e.g. Leaking pipe in basement',
  ir_visibility: 'Visibility',
  ir_vis_personal: 'Personal',
  ir_vis_community: 'Community',
  ir_vis_help_community: 'Other residents can see and upvote this.',
  ir_vis_help_personal: 'Only you and the helpdesk can see this.',
  ir_notify: 'Notify',
  ir_notify_wing: 'My wing',
  ir_notify_all: 'All residents',
  ir_notify_helpdesk: 'Helpdesk',
  ir_desc_ph: "What's wrong, and where?",
  ir_submit_error: 'Could not submit the issue right now. Try again in a bit.',
  ir_submit: 'Submit issue',

  // events/create (create form)
  ec_title: 'Create event',
  ec_discard: 'Discard',
  ec_add_cover: 'Add cover image',
  ec_title_label: 'Event title',
  ec_title_ph: 'e.g. Diwali celebration',
  ec_date: 'Date',
  ec_date_ph: '9 Nov 2026',
  ec_time: 'Time',
  ec_time_ph: '7:00 PM',
  ec_venue: 'Venue',
  ec_venue_ph: 'Clubhouse lawn',
  ec_desc_ph: "What's happening?",
  ec_submit_error: 'Could not submit the event right now. Try again in a bit.',
  ec_submit: 'Submit event',

  // staff (directory + register)
  staff_title: 'My staff & help',
  staff_register_new: 'Register new staff',
  staff_info_note: "Registered help can enter with their personal code. You'll see in/out times.",
  staff_error_title: 'Staff list unavailable',
  staff_error_body: 'We could not load your staff right now. Try again in a bit.',
  staff_section_mine: 'My staff',
  staff_empty_mine: 'No staff linked to your flat yet. Add one below, or register someone new with +.',
  staff_section_directory: 'Works in this society',
  staff_directory_hint: 'Already registered by neighbours — add them to your flat in one tap.',
  staff_view_attendance: 'View attendance log',
  staff_add: 'Add',
  staff_adding: 'Adding…',
  staff_add_error: 'Could not add {name}. Try again in a bit.',
  staff_register_error: 'Could not register them right now. Try again in a bit.',
  staff_sheet_title: 'Register household help',
  staff_field_name: 'Name',
  staff_name_ph: 'e.g. Sunita Bai',
  staff_field_role: 'Role',
  staff_field_phone: 'Phone (optional)',
  staff_field_schedule: 'Schedule (optional)',
  staff_schedule_ph: 'Mon–Sat · 9–11 AM',
  staff_sheet_note: 'They get a personal gate code — guards use it to log their in/out times.',
  staff_register: 'Register',
  // staff roles (picker chips — value stays English)
  staff_role_maid: 'Maid',
  staff_role_cook: 'Cook',
  staff_role_driver: 'Driver',
  staff_role_nanny: 'Nanny',
  staff_role_presswala: 'Presswala',
  // staff presence
  staff_presence_inside: 'INSIDE',
  staff_presence_left: 'LEFT',
  staff_presence_not_in: 'NOT IN',

  // staff ID card (detail)
  sid_title: 'Staff ID card',
  sid_more_options: 'More options',
  sid_error_title: 'Staff details unavailable',
  sid_notfound_title: 'Staff member not found',
  sid_error_body: 'We could not load this ID card right now.',
  sid_badge: 'Staff ID',
  sid_id_verified: 'ID verified',
  sid_verification_pending: 'Verification pending',
  sid_works_for: 'Works for',
  sid_gate_code: 'Gate code',
  sid_this_month: 'This month',
  sid_days_in_one: '{n} day in',
  sid_days_in_many: '{n} days in',
  sid_updating: 'Updating…',
  sid_change_photo: 'Change photo',
  sid_add_photo: 'Add photo',
  sid_photo_error: 'Could not update the photo. Try again in a bit.',
  sid_no_ratings: 'No ratings yet',
  sid_rating_summary_one: '{avg} · {n} rating',
  sid_rating_summary_many: '{avg} · {n} ratings',
  sid_you_rated_one: 'You rated {n} star — tap to change',
  sid_you_rated_many: 'You rated {n} stars — tap to change',
  sid_rate_prompt: 'Rate their work for your flat',
  sid_record_payment: 'Record payment',
  sid_record_payment_sub: 'Keep a private salary log for your flat',
  sid_attendance_summary: 'Attendance summary',
  sid_attendance_summary_sub: 'In/out log for this member',
  sid_removing: 'Removing…',
  sid_remove_from_flat: 'Remove from my flat',
  sid_remove_note: 'They stay registered with the society — this only unlinks them from your flat.',
  sid_remove_error: 'Could not remove them from your flat. Try again in a bit.',
  sid_rate_title: "How is {name}'s work?",
  sid_rate_note_label: 'Anything to add? (optional)',
  sid_rate_note_ph: 'Only your society admin can see this',
  sid_save_rating: 'Save rating',
  sid_pay_title: 'Record a payment',
  sid_amount_label: 'Amount (₹)',
  sid_note_label: 'Note (optional)',
  sid_pay_note_ph: 'July salary, Diwali bonus…',
  sid_record: 'Record',
  sid_recent: 'RECENT',
  sid_entry_remove_error: 'Could not remove that entry. Try again in a bit.',
  sid_pay_error: 'Could not record the payment. Try again in a bit.',
  sid_rating_error: 'Could not save the rating. Try again in a bit.',
  sid_pay_note_footer: 'Private to your flat — a personal ledger, not a transfer.',

  // forgot-password (fgp_*)
  fgp_title: 'Reset your password',
  fgp_subtitle: "We'll email you a reset link. Magic-link sign-in never needs a password.",
  fgp_send: 'Email reset link',
  fgp_back_to_signin: 'Back to sign in',
  fgp_check_title: 'Check your email',
  fgp_check_body: "If an account exists for {email}, you'll receive a reset link shortly.",

  // reset-password (rp_*)
  rp_title: 'Set a new password',
  rp_for: 'For {who}',
  rp_your_account: 'your account',
  rp_new_password_label: 'New password',
  rp_confirm_label: 'Confirm password',
  rp_min: 'At least 8 characters with a number.',
  rp_mismatch: 'Passwords don’t match.',
  rp_link_invalid: 'This reset link is invalid or has expired.',
  rp_save: 'Save new password',
  rp_strength_weak: 'Weak',
  rp_strength_good: 'Good',
  rp_strength_strong: 'Strong',
  rp_done_title: 'Password reset',
  rp_done_body: 'Please sign in with your new password.',

  // magic-sent (ms_*)
  ms_title: 'Check your mail',
  ms_body_before: 'We sent a sign-in link to',
  ms_body_after: '. Open the email on this device and tap the link to continue.',
  ms_your_email: 'your email address',
  ms_open_mail: 'Open mail app',
  ms_resend_countdown: "Didn't receive it? Resend ({secs}s)",
  ms_resend: 'Resend link',

  // login continue
  login_continue: 'Continue',
  // verify-code (vc_*)
  vc_title: 'Enter your code',
  vc_sent_to: 'We sent a 6-digit code to {email}. Enter it below to sign in.',
  vc_code_label: '6-digit code',
  vc_verify: 'Verify',
  vc_resend: 'Resend code',
  vc_resend_in: 'Resend in {s}s',
  vc_err_invalid: 'That code is invalid or has expired.',
  vc_err_rate: 'Too many attempts. Please wait a moment and try again.',
  vc_err_generic: 'Something went wrong. Please try again.',

  // pay/success (ps_*)
  ps_paid_thanks: 'Paid, thank you',
  ps_received: 'Payment received',
  ps_paid_via: 'Paid via',
  ps_reference: 'Reference',
  ps_time: 'Time',
  ps_receipt: 'Receipt',
  ps_download_pdf: 'Download PDF',
  ps_done: 'Done',

  // +not-found (nf_*)
  nf_title: 'This gate leads nowhere',
  nf_body: "The page you're looking for doesn't exist, or may have moved.",

  // CompanyPicker (cp_*)
  cp_more: 'More…',
  cp_which_cab: 'Which cab service?',
  cp_which_company: 'Which company?',
  cp_search_ph: 'Search companies',
  cp_use: 'Use “{q}”',
  cp_no_companies: 'No companies.',

  // meetings — shared/index (mtg_*)  [bye-law/statute copy LEFT English by design]
  nav_new: 'New',
  mtg_kind_mc: 'COMMITTEE',
  mtg_kind_agm: 'AGM',
  mtg_kind_sgm: 'SGM',
  mtg_status_draft: 'DRAFT',
  mtg_status_noticed: 'NOTICED',
  mtg_status_held: 'HELD',
  mtg_status_minutes_due: 'MINUTES DUE',
  mtg_status_closed: 'CLOSED',
  mtg_status_cancelled: 'CANCELLED',
  mtg_title: 'Meetings',
  mtg_forbidden_title: 'Committee only',
  mtg_forbidden_body: 'Meetings are run by the committee and office bearers. Ask your committee if you should have access.',
  mtg_err_title: "Couldn't load meetings",
  mtg_agm_due: 'AGM due in {days} days — by {date}',
  mtg_agm_overdue: 'AGM overdue by {days} days',
  mtg_empty_title: 'No meetings yet',
  mtg_empty_cta: 'Schedule a meeting',
  mtg_quorum_count: 'quorum {p}/{r}',
  mtg_not_met: 'NOT met',

  // meetings/new (mtn_*)  [KINDS rule/quorum/label + 'clear days' + bye-law 120 copy LEFT English]
  mtn_title: 'Schedule meeting',
  mtn_err_date: 'Date must be YYYY-MM-DD, e.g. 2026-07-28.',
  mtn_err_time: 'Time must be HH:MM (24h), e.g. 18:30.',
  mtn_err_generic: 'Could not schedule the meeting.',
  mtn_f_title: 'Title',
  mtn_f_title_help: 'Defaults to "{label}"',
  mtn_f_date: 'Date',
  mtn_f_time: 'Time (24h)',
  mtn_f_venue: 'Venue',
  mtn_ph_venue: 'Society office',
  mtn_agenda: 'Agenda',
  mtn_ph_item: 'Item {n}',
  mtn_a11y_recusal: 'Toggle conflict-of-interest recusal',
  mtn_recusal: 'Recusal',
  mtn_remove_item: 'Remove item',
  mtn_add_item: '+ Add item',
  mtn_scheduling: 'Scheduling…',

  // meetings/[id] (mtd_*)  [clear-days + all bye-law 136 copy + resolution-validity copy LEFT English]
  mtd_title_meeting: 'Meeting',
  mtd_err_load: "Couldn't load this meeting",
  mtd_notice: 'Notice',
  mtd_business: 'Business',
  mtd_recusal: 'RECUSAL',
  mtd_quorum: 'Quorum',
  mtd_required_present: 'Required: {n} present',
  mtd_present_quorum: '{p}/{r} present — quorum {verdict}',
  mtd_met: 'met',
  mtd_not_met: 'NOT met',
  mtd_certified_by: 'Certified by {name}',
  mtd_the_committee: 'the committee',
  mtd_recorded_suffix: ' · recorded by {name}',
  mtd_minutes: 'Minutes',
  mtd_not_recorded: 'Not recorded yet.',
  mtd_working: 'Working…',
  mtd_issue_notice: 'Issue notice',
  mtd_ok_notice: 'Notice issued and members informed.',
  mtd_mark_held: 'Mark as held',
  mtd_record_minutes: 'Record minutes',
  mtd_edit_minutes: 'Edit minutes',
  mtd_certify_meeting: 'Certify meeting',
  mtd_cancel_meeting: 'Cancel meeting',
  mtd_ok_cancel: 'Meeting cancelled.',
  mtd_done: 'Done.',
  mtd_err_generic: 'Could not do that.',
  mtd_who_present: 'Who was present?',
  mtd_quorum_needs: 'Quorum needs {q} of {total} committee members.',
  mtd_members_present: 'Members present',
  mtd_quorum_help: 'Quorum: {n}',
  mtd_ph_headcount: 'e.g. 24',
  mtd_recording: 'Recording…',
  mtd_record_attendance: 'Record attendance',
  mtd_ok_attendance: 'Attendance recorded — awaiting a second member to certify.',
  mtd_minutes_of_meeting: 'Minutes of the meeting',
  mtd_ph_minutes: 'Decisions taken, votes, recusals observed…',
  mtd_saving: 'Saving…',
  mtd_save_minutes: 'Save minutes',
  mtd_ok_minutes: 'Minutes saved as draft.',
  mtd_certify_the_meeting: 'Certify the meeting',
  mtd_cert_attendance: 'Attendance & quorum — {p}/{r} present, quorum {verdict}',
  mtd_cert_minutes: 'Minutes as recorded above',
  mtd_certifying: 'Certifying…',
  mtd_certify_full: 'Certify attendance, quorum & minutes',
  mtd_ok_certified: 'Meeting certified.',

  // elections/index (el_*)  [bye-law 115(a) empty-body + SCEA/self-run mode pills LEFT English]
  el_phase_announced: 'ANNOUNCED',
  el_phase_nominations: 'NOMINATIONS OPEN',
  el_phase_scrutiny: 'SCRUTINY',
  el_phase_voting: 'VOTING',
  el_phase_completed: 'COMPLETED',
  el_phase_cancelled: 'CANCELLED',
  el_title: 'Elections',
  el_err_title: "Couldn't load elections",
  el_empty_title: 'No elections yet',
  el_intro: 'Committee elections — every member can see nominations, scrutiny and results.',
  el_seat_one: '{n} seat',
  el_seat_other: '{n} seats',
  el_term: 'term {t}',
  el_cand_one: '{n} candidate',
  el_cand_other: '{n} candidates',
  el_polls: 'polls {d}',

  // elections/new (eln_*)  [MODES label/rule + Sec.154B-20 reserved-seat copy LEFT English]
  eln_title: 'Announce election',
  eln_err_title: 'Give the election a title.',
  eln_err_seats: 'Seats must be a number from 1 to 50.',
  eln_err_date: '{label} must be YYYY-MM-DD.',
  eln_err_generic: 'Could not announce the election.',
  eln_f_title: 'Title',
  eln_ph_title: 'Managing Committee 2026–2031',
  eln_f_seats: 'Seats',
  eln_ph_seats: 'e.g. 5',
  eln_f_term: 'Term',
  eln_f_reserved: 'Reserved seats',
  eln_f_nomclose: 'Nominations close',
  eln_f_votedate: 'Voting date',
  eln_announcing: 'Announcing…',

  // elections/[id] (eld_*)  [SCEA/self-run mode + eligibility-rule + State Co-op Election Authority copy LEFT English]
  eld_adv_announced: 'Open nominations',
  eld_adv_nominations: 'Close nominations — start scrutiny',
  eld_adv_scrutiny: 'Open voting',
  eld_title: 'Election',
  eld_err_load: "Couldn't load this election",
  eld_seat_one: '{n} seat',
  eld_seat_other: '{n} seats',
  eld_term: 'term {t}',
  eld_nom_close: 'nominations close {d}',
  eld_polls: 'polls {d}',
  eld_stand: 'Stand for election',
  eld_nom_in: 'Your nomination is in — scrutiny comes next.',
  eld_ballot_in: "Your ballot is in. Results appear when they're declared.",
  eld_results: 'Results',
  eld_candidates: 'Candidates · {n}',
  eld_no_noms: 'No nominations yet.',
  eld_member: 'Member',
  eld_proposed_by: 'Proposed by {name}',
  eld_pill_elected: 'ELECTED',
  eld_pill_rejected: 'REJECTED',
  eld_pill_on_ballot: 'ON BALLOT',
  eld_pill_pending: 'PENDING',
  eld_vote_one: '{n} vote',
  eld_vote_other: '{n} votes',
  eld_flagged: 'Flagged: {note}',
  eld_accept: 'Accept',
  eld_ok_accepted: '{name} is on the ballot.',
  eld_reject: 'Reject',
  eld_ok_rejected: '{name} rejected on scrutiny.',
  eld_vote_for: 'Vote for {name}',
  eld_working: 'Working…',
  eld_ok_advanced: 'Phase advanced.',
  eld_declare: 'Declare results',
  eld_ok_declared: 'Results declared.',
  eld_constitute: 'Constitute the committee',
  eld_ok_constituted: 'Winners added to the Committee Member position.',
  eld_cancel_election: 'Cancel election',
  eld_ok_cancelled: 'Election cancelled.',
  eld_f_statement: 'Statement',
  eld_f_statement_help: 'Optional — why members should vote for you',
  eld_ph_statement: "What you'll focus on for the society",
  eld_submitting: 'Submitting…',
  eld_submit_nom: 'Submit nomination',
  eld_ok_nom: 'Nomination submitted.',
  eld_cast_ballot: 'Cast your ballot',
  eld_vote_confirm: 'Vote for {name}?',
  eld_ballot_rules: "One ballot per member; it can't be changed. The tally stays sealed until results are declared.",
  eld_casting: 'Casting…',
  eld_cast_btn: 'Cast ballot',
  eld_ok_ballot: 'Ballot cast.',
  eld_recording: 'Recording…',
  eld_record_result: 'Record result',
  eld_ok_recorded: 'Results recorded.',
  eld_err_generic: 'Could not do that.',

  // finances (fin_*)
  fin_cat_maintenance: 'Maintenance',
  fin_cat_repairs: 'Repairs',
  fin_cat_security: 'Security',
  fin_cat_utilities: 'Utilities',
  fin_cat_housekeeping: 'Housekeeping',
  fin_cat_events: 'Events',
  fin_cat_admin: 'Admin',
  fin_cat_other: 'Other',
  fin_err_amount: 'Enter the amount in rupees.',
  fin_err_note: 'Say what the expense was for.',
  fin_err_date: 'Date must be YYYY-MM-DD.',
  fin_ok_recorded: '{amount} recorded under {cat}.',
  fin_err_record: 'Could not record the expense.',
  fin_ok_voided: '{amount} entry voided — recorded on the audit trail.',
  fin_err_void: 'Could not void.',
  fin_err_receipt: 'Could not open the receipt.',
  fin_title: 'Society finances',
  fin_expense_btn: 'Expense',
  fin_err_load: "Couldn't load the finances",
  fin_intro: 'Every rupee in and out of the society, month by month. Receipts are recorded by the treasurer; expenses carry receipts and land on the audit trail.',
  fin_monthly_statement: 'Monthly statement',
  fin_collected: 'Collected',
  fin_spent: 'Spent',
  fin_ledger_title: 'Expense ledger · committee view',
  fin_no_expenses: 'No expenses recorded yet.',
  fin_receipt: 'Receipt',
  fin_void: 'Void',
  fin_record_expense: 'Record an expense',
  fin_f_amount: 'Amount (₹)',
  fin_ph_amount: 'e.g. 4200',
  fin_f_date: 'Date',
  fin_f_date_help: 'Blank = today',
  fin_f_what: 'What for?',
  fin_ph_what: 'e.g. lift AMC — Q2 invoice',
  fin_f_receipt: 'Receipt photo',
  fin_f_receipt_help: 'Optional — good bookkeeping',
  fin_recording: 'Recording…',
  fin_record_expense_btn: 'Record expense',

  // ---- marketing homepage (app/index.tsx) ----
  mkt_nav_gate: 'Gate security',
  mkt_nav_community: 'Community',
  mkt_nav_payments: 'Payments',
  mkt_nav_pricing: 'Pricing',
  mkt_open_app: 'Open app →',
  mkt_request_demo: 'Request a demo',
  mkt_see_how: 'See how it works',
  mkt_hero_title: 'Every gate in your society, one calm app.',
  mkt_hero_sub: 'Bero replaces the noisy gate apps. Visitors, notices, payments and bookings for Indian housing societies — with no ads, ever.',
  mkt_f_gate_title: 'Approve visitors in one tap, from anywhere.',
  mkt_f_gate_body: 'Guards capture a photo and plate at the gate; residents get a full-screen approval with a countdown. Pre-approved guests walk in with a QR pass or a 4-digit PIN.',
  mkt_f_gate_p1: 'Full-screen approval takeover',
  mkt_f_gate_p2: 'Ticket-style QR + PIN gate passes',
  mkt_f_gate_p3: 'Staff attendance with punch in/out',
  mkt_f_gate_mock: 'At your gate',
  mkt_f_comm_title: 'Notices people actually read.',
  mkt_f_comm_body: 'Urgent notices require acknowledgement, polls close with clean results, and events collect RSVPs by flat. Discussions stay civil because everyone is a verified neighbour.',
  mkt_f_comm_p1: 'Acknowledgement tracking by flat',
  mkt_f_comm_p2: 'One-vote-per-flat polls',
  mkt_f_comm_p3: 'Verified residents only',
  mkt_f_pay_title: 'Maintenance paid on time, without nagging.',
  mkt_f_pay_body: 'Clear invoices with line-item breakdowns, UPI in two taps, instant receipts, and a committee dashboard that shows exactly who has paid — so reminders can be gentle and rare.',
  mkt_f_pay_p1: 'UPI, cards and auto-pay',
  mkt_f_pay_p2: 'Instant PDF receipts',
  mkt_f_pay_p3: 'Committee collections dashboard',
  mkt_f_pay_mock: 'July maintenance',
  mkt_m_delivery_waiting: 'Delivery · waiting',
  mkt_m_guest_pass: 'Guest pass {code}',
  mkt_m_housekeeping: 'Housekeeping · on duty',
  mkt_m_urgent_notice: 'Urgent notice',
  mkt_m_poll_voted: 'Poll · {n} voted',
  mkt_m_maint_sinking: 'Maintenance + sinking fund',
  mkt_m_paid_via: 'Paid {date} · UPI',
  mkt_m_collections: 'Collections {pct}',
  mkt_m_of_flats: '{a} of {b} flats',
  mkt_allow: 'Allow',
  mkt_valid: 'Valid',
  mkt_rsvp: 'RSVP',
  mkt_pay: 'Pay',
  mkt_paid: 'Paid',
  mkt_live: 'Live',
  mkt_manifesto_title: 'Your society is a home, not an audience.',
  mkt_manifesto_body: 'No ads. No sponsored banners. No "deals near you". No selling your data to brokers. Bero is paid for by societies, so residents are the customer — never the product.',
  mkt_stat_ads: 'Ads shown',
  mkt_stat_brokers: 'Data brokers',
  mkt_stat_funded: 'Society-funded',
  mkt_pricing_kicker: 'Simple pricing',
  mkt_pricing_per: 'per flat / month',
  mkt_pricing_body: 'Everything included — gate, community, payments, guard kiosk. No tiers, no lock-in.',
  mkt_pp1: 'Unlimited residents, guards and admins',
  mkt_pp2: 'Guard tablet kiosk included',
  mkt_pp3: 'Hindi, Marathi and English',
  mkt_pp4: 'Data stays in India, never sold',
  mkt_demo_title: 'See Bero at your own gate.',
  mkt_demo_body: 'A 30-minute walkthrough with your committee. We set up a sandbox with your wings and flats.',
  mkt_link_privacy: 'Privacy',
  mkt_link_terms: 'Terms',
  mkt_link_contact: 'Contact',
  mkt_footer_copy: '© 2026 Bero Technologies Pvt Ltd, Mumbai',

  // ---- public gate-pass page (app/p/[code].tsx) ----
  pp_loading: 'Loading pass…',
  pp_invalid_title: 'This pass link is invalid',
  pp_invalid_body: 'It may have expired or been cancelled. Ask your host for a fresh link.',
  pp_or_tell_guard: 'Or tell the guard',
  pp_visiting: 'Visiting',
  pp_valid_label: 'Valid',
  pp_daily: 'Daily',
  pp_footer: 'Show this at the gate. Guards verify it automatically — no app needed.',
  pp_status_active: 'ACTIVE',
  pp_status_upcoming: 'UPCOMING',
  pp_status_expired: 'EXPIRED',
  pp_status_cancelled: 'CANCELLED',
  pp_multi_entry: 'multi entry',
  pp_single_entry: 'single entry',
  pp_valid_until: 'valid until {time}',

  // ---- public party-invite page (app/i/[code].tsx) ----
  pi2_loading: 'Loading invite…',
  pi2_invalid_title: 'This invite link is invalid',
  pi2_invalid_body: 'It may have been cancelled. Ask your host for a fresh link.',
  pi2_society_invite: '{society} · Invite',
  pi2_claim_prompt: 'Enter your name to get your personal gate pass{extra}.',
  pi2_n_left: ' — {n} left',
  pi2_name_ph: 'Your name',
  pi2_get_pass: 'Get my gate pass',
  pi2_getting: 'Getting your pass…',
  pi2_closed_full: 'All guest passes for this invite are taken. Ask your host directly.',
  pi2_closed_expired: 'This invite has expired. Ask your host for a fresh link.',
  pi2_closed_cancelled: 'The host cancelled this invite.',
  pi2_err_generic: 'Could not get your pass. Try again.',
  pi2_err_network: 'Could not get your pass. Check your connection and retry.',
  pi2_footer: 'Your pass has a QR and a PIN — show either at the gate. No app needed.',
  // desktop sidebar nav
  nav_profile: 'Profile',
  nav_vendors: 'Vendors',
  nav_classifieds: 'Classifieds',
  nav_lost_found: 'Lost & found',
  nav_birthdays: 'Birthdays',
  nav_elections: 'Elections',
  nav_preapprove: 'Pre-approve visitor',
  nav_gate_pass: 'Gate pass',
  nav_visitor_history: 'Visitor history',
  nav_household_staff: 'Household staff',
  nav_parcels: 'Parcels',
  nav_vehicles: 'Vehicles',
  nav_helpdesk: 'Helpdesk',
  nav_amenities: 'Amenities',
  nav_venue_booking: 'Venue booking',
  nav_my_bookings: 'My bookings',
  nav_documents: 'Documents',
  nav_residents: 'Residents',
  nav_grp_management: 'Management',
  nav_society_admin: 'Society admin',
  nav_committee_desk: 'Committee desk',
  nav_grp_living: 'Living',
  nav_signed_in: 'Signed in',
  // status pills
  pill_open: 'OPEN',
  pill_progress: 'IN PROGRESS',
  pill_urgent: 'URGENT',
  pill_resolved: 'RESOLVED',
  pill_approved: 'APPROVED',
  pill_pending: 'PENDING',
  pill_rejected: 'REJECTED',
  pill_awaiting: 'AWAITING',
  pill_at_gate: 'AT GATE',
  pill_left: 'LEFT',
  pill_inside: 'INSIDE',
  pill_reported: 'REPORTED',
  pill_picked_up: 'PICKED UP',
  pill_primary: 'PRIMARY',
  pill_lost: 'LOST',
  pill_found: 'FOUND',
  pill_event: 'EVENT',
  pill_committee: 'COMMITTEE',
  pill_general: 'GENERAL',
  pill_cancelled: 'CANCELLED',
  pill_ended: 'ENDED',
  pill_full: 'FULL',
  pill_active_now: 'ACTIVE NOW',
  pill_upcoming: 'UPCOMING',
  pill_expired: 'EXPIRED',
  gp_pass_id: 'Pass ID',
  home_maint_due: 'Maintenance due',
  home_nothing_due: 'Nothing due right now.',
  home_pay_maint: 'Pay maintenance',
  home_community_empty: 'Nothing new from the community.',
  svc_booked: 'Booked',
  // platform console (Bero staff) — Task 6: foundations
  plat_title: 'Bero Platform',
  plat_nav_societies: 'Societies',
  plat_nav_leads: 'Leads',
  plat_nav_create: 'Create society',
  plat_nav_audit: 'Audit log',
  plat_nav_members: 'Team',
  plat_gate_denied_title: 'Bero staff only',
  plat_gate_denied_body: 'This console is for the Bero platform team.',
  plat_acting_banner: 'Viewing {society} as Bero {role}',
  plat_acting_exit: 'Exit',
  // platform console — Task 7: societies list + profile + enter
  plat_soc_search_ph: 'Search name or city',
  plat_soc_col_society: 'Society',
  plat_soc_col_plan: 'Plan',
  plat_soc_col_flats: 'Flats',
  plat_soc_col_residents: 'Residents',
  plat_soc_col_admins: 'Admins',
  // Singular forms for the stacked (narrow) societies row — "1 admins" reads
  // like a bug. hi/mr intentionally fall back to EN here: the console is
  // internal, English-primary (see the platform-console spec).
  plat_soc_one_flat: 'flat',
  plat_soc_one_resident: 'resident',
  plat_soc_one_admin: 'admin',
  plat_soc_col_activity: 'Last activity',
  plat_soc_empty: 'No societies match.',
  plat_soc_err: 'Could not load societies.',
  plat_prof_admins: 'Society admins',
  plat_prof_enter: 'Enter society',
  plat_prof_entering: 'Entering…',
  plat_prof_plan: 'Plan',
  plat_prof_stats: 'At a glance',
  plat_enter_note_ph: 'Why are you entering? (optional, audited)',
  plat_enter_err: 'Could not enter this society.',
  // platform console — Task 8: create society + claim-owner
  plat_create_title: 'Create society',
  plat_create_society_sec: 'Society',
  plat_create_owner_sec: 'Owner (gets the invite email)',
  plat_create_name: 'Society name',
  plat_create_city: 'City',
  plat_create_address: 'Address',
  plat_create_slug: 'Slug',
  plat_create_slug_help: 'Auto-generated — edit only if needed',
  plat_create_plan: 'Plan',
  plat_create_owner_name: 'Owner full name',
  plat_create_owner_email: 'Owner email',
  plat_create_submit: 'Create & send invite',
  plat_create_creating: 'Creating…',
  plat_create_done_title: 'Society created',
  plat_create_done_body: 'Invite sent to {email}. They set their own password and land as the society admin.',
  plat_create_email_failed: 'Society created, but the invite email failed.',
  plat_create_resend: 'Re-send invite',
  plat_create_err_generic: 'Could not create the society.',
  plat_create_err_name: 'Society name is required.',
  plat_create_err_city: 'City is required.',
  plat_create_err_owner_name: "Owner's full name is required.",
  co_title: 'Set your password',
  co_sub: '{name}, you are taking charge of {society} on Bero.',
  // Staff invites reuse this screen; the society line makes no sense for them.
  co_sub_staff: '{name}, you have been given {role} access to the Bero platform console.',
  co_password: 'Password',
  co_confirm: 'Confirm password',
  co_mismatch: "Passwords don't match.",
  co_min: 'At least 8 characters.',
  co_submit: 'Set password & sign in',
  co_invalid_title: 'This invite link is not valid',
  co_invalid_body: 'It may have expired or already been used. Ask Bero to send a fresh one.',
  co_err_generic: 'Could not set your password. Try again.',
  // platform console — Task 9: leads, audit, members
  plat_leads_title: 'Leads & prospects',
  plat_leads_empty: 'No leads yet — they appear when societies ask for Bero.',
  plat_leads_err: 'Could not load leads.',
  plat_lead_status_new: 'New',
  plat_lead_status_contacted: 'Contacted',
  plat_lead_status_converted: 'Converted',
  plat_lead_status_closed: 'Closed',
  plat_lead_stage_lead: 'Lead',
  plat_lead_stage_prospect: 'Prospect',
  plat_lead_notes_ph: 'Notes (visible to the team)',
  plat_lead_convert: 'Convert → create society',
  plat_lead_save_err: 'Could not save the lead.',
  plat_audit_title: 'Audit log',
  plat_audit_empty: 'Nothing logged yet.',
  plat_audit_err: 'Could not load the audit log.',
  plat_audit_action_all: 'All',
  plat_audit_action_enter_society: 'Entered society',
  plat_audit_action_create_society: 'Created society',
  plat_audit_action_plan_change: 'Plan change',
  plat_audit_action_lead_update: 'Lead update',
  plat_audit_action_invite_sent: 'Invite sent',
  plat_audit_action_invite_accepted: 'Invite accepted',
  plat_audit_action_member_change: 'Team change',
  plat_members_title: 'Platform team',
  plat_members_add: 'Add member',
  plat_members_email: 'Work email',
  plat_members_name: 'Full name',
  plat_members_name_help: 'Needed only when inviting someone new.',
  plat_members_invite: 'Send invite',
  plat_members_invite_sent: 'Invite sent to {email} — they set their own password. Valid 72 hours.',
  plat_members_invite_failed: 'Member added, but the invite email failed to send. Use Resend.',
  plat_members_pending: 'Invited — waiting for them to set a password',
  plat_members_resend: 'Resend',
  plat_members_revoke: 'Cancel invite',
  plat_members_role_help: 'Only Super admin can enter societies, create societies, change plans, see the audit log and manage this team.',
  plat_members_role: 'Role',
  plat_members_err: 'Could not update the team.',
  plat_members_deactivate: 'Deactivate',
  plat_members_activate: 'Activate',
  plat_members_empty: 'No platform team members yet.',
  plat_role_super_admin: 'Super admin',
  plat_role_sales: 'Sales',
  plat_role_support: 'Support',
  plat_role_marketing: 'Marketing',
  // Society switcher (multi-society)
  sw_switch_society: 'Switch society',
  sw_your_society: 'Your society',
  sw_add_flat: 'Add a flat',
  // Profile — Account section (sec_account already defined above)
  row_manage_flats: 'Your flats & societies',
  row_deactivate: 'Deactivate account',
  row_delete_account: 'Delete account',
  // Delete account (permanent)
  del_title: 'Delete account',
  del_heading: 'Delete your Bero account?',
  del_body: 'This is permanent and cannot be undone.',
  del_point_signin: 'You will be signed out on every device and can no longer sign in.',
  del_point_flats: 'You will be removed from your societies and flats.',
  del_point_records: 'Records your society must keep by law (payments, meeting minutes) are retained, no longer linked to your name.',
  del_confirm_pw: 'Enter your password to confirm',
  del_password_error: 'Please enter your password.',
  del_wrong_password: 'That password is incorrect.',
  del_generic_error: 'Could not delete your account. Please try again.',
  del_confirm_button: 'Delete my account',
  // Account-first signup
  ca_title: 'Create your account',
  ca_sub: 'One account for every society you live in. You’ll add your flat next.',
  ca_submit: 'Create account',
  ca_next_note: 'Next: find your society and flat.',
  // Manage flats / societies
  mfl_title: 'Your flats',
  mfl_add_flat: 'Add a flat',
  mfl_a_society: 'A society',
  mfl_status_approved: 'Active',
  mfl_status_pending: 'Pending approval',
  mfl_status_rejected: 'Not approved',
  mfl_withdraw: 'Withdraw request',
  mfl_leave: 'Leave society',
  mfl_empty_title: 'No flats yet',
  mfl_empty_body: 'Add your flat to join your society on Bero.',
  mfl_err: 'Could not load your flats.',
  // Add a flat
  af_title: 'Add a flat',
  af_find_society: 'Find your society',
  af_search_ph: 'Search by society name',
  af_no_match: 'No society found. Ask your society to get on Bero.',
  af_change_society: 'Change society',
  af_pick_flat: 'Pick your flat',
  af_no_flats: 'No flats listed for this society yet.',
  af_you_are: 'You are the',
  af_rt_owner: 'Owner',
  af_rt_tenant: 'Tenant',
  af_rt_family: 'Family',
  af_submit: 'Send request',
  af_admin_note: 'Your society admin will review and approve this.',
  af_err: 'Could not send the request. Try again.',
  af_done_title: 'Request sent',
  af_done_body: 'Your request to join {society} is with the society admin. You’ll be able to switch to it once approved.',
  af_done_cta: 'Back to your flats',
} as const;

export type DictKey = keyof typeof en;
export type Dict = Partial<Record<DictKey, string>>;
